export interface ActivityYearGroup<T> {
  year: number;
  activities: T[];
}

/**
 * Year is read from the calendar date written in `endDate` itself
 * (leading `YYYY-` of the ISO string), so it never shifts with the
 * runtime timezone. Non-ISO input falls back to the UTC year.
 */
const yearOf = (endDate: string): number =>
  /^\d{4}-/.test(endDate)
    ? Number(endDate.slice(0, 4))
    : new Date(endDate).getUTCFullYear();

// ISO strings compare chronologically as plain strings; no locale involved.
const byEndDateDesc = (
  a: { endDate: string },
  b: { endDate: string },
): number => (a.endDate < b.endDate ? 1 : a.endDate > b.endDate ? -1 : 0);

/**
 * Groups activities by the calendar year of `endDate`, years newest first,
 * activities within a year most recently finished first.
 *
 * Pure. Pass the whole accumulated list after each incremental page load —
 * grouping the accumulation is what keeps a year to exactly one group.
 */
export function groupActivitiesByYear<T extends { endDate: string }>(
  activities: readonly T[],
): ActivityYearGroup<T>[] {
  const buckets = new Map<number, T[]>();

  for (const activity of activities) {
    const year = yearOf(activity.endDate);
    const bucket = buckets.get(year);
    if (bucket) {
      bucket.push(activity);
    } else {
      buckets.set(year, [activity]);
    }
  }

  return [...buckets]
    .sort(([a], [b]) => b - a)
    .map(([year, items]) => ({ year, activities: items.sort(byEndDateDesc) }));
}
